// src/pages/lab/processing/processingtabs/components/stepExecution/UploadedResultSummary.tsx
//
// The expanded body under a result row: what was uploaded, the files, the notes and the
// analysis feedback for the result type. A gsa_raw result also embeds the genotyper run panel.

import React, { useState } from "react";
import { Alert, Button, Descriptions, Spin, Typography } from "antd";
import type { DescriptionsProps } from "antd";
import { DownloadOutlined, FileTextOutlined } from "@ant-design/icons";
import type { ColumnsType } from "antd/es/table";

import { DataTable, StatusTag } from "../../../../../../components/shared";
import GenotyperRunPanel from "./GenotyperRunPanel";

import { downloadResultFile } from "../../../../../../api/labiq/SampleResultApi";

import {
	getResultTypeLabel,
	isGeneCodesAnalysisResults,
	isGsaRawAnalysisSummary,
} from "../../../../../../types/lab/SampleResultTypes";
import type {
	GeneCodesAnalysisResults,
	GeneCodesSampleSummary,
	GsaRawAnalysisSummary,
	SampleResult,
	VerificationStatus,
} from "../../../../../../types/lab/SampleResultTypes";

import { useNotifications } from "../../../../../../context/NotificationContext";
import { formatFileSize } from "../../../../../../utils/fileUtils";
import { formatCallRate, isLowCallRate } from "../../../../../../utils/genotyper";
import { formatInZone } from "../../../../../../utils/time";

const { Text } = Typography;

interface UploadedResultSummaryProps {
	result: SampleResult;
	// Forwarded to the genotyper panel so a re-run refreshes the results list.
	onRerun?: () => void;
}

const VERIFICATION_LABELS: Record<VerificationStatus, string> = {
	pending: "Verification pending",
	verified: "Verified",
	rejected: "Rejected",
};

const PROCESSING_TYPE_LABELS: Record<string, string> = {
	SampleProtocolInstance: "Sample processing",
	AnalysisProtocolInstance: "Analysis processing",
	ProcessingGroup: "Group processing",
	Sample: "Sample processing",
	group: "Group processing",
	individual: "Individual processing",
};

const LABEL_STYLE: React.CSSProperties = { fontSize: 10, textTransform: "uppercase", letterSpacing: 0.4, color: "var(--mg-text-secondary)" };
const SM_TEXT: React.CSSProperties = { fontSize: 12 };
const SM_LABEL: React.CSSProperties = { fontSize: 11, color: "var(--mg-text-secondary)" };

const SectionLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => <div style={{ ...LABEL_STYLE, marginBottom: 4 }}>{children}</div>;

type DescPair = [label: string, value: React.ReactNode];

const descItems = (pairs: DescPair[]): DescriptionsProps["items"] =>
	pairs.map(([label, value]) => ({
		key: label,
		label: <Text style={SM_LABEL}>{label}</Text>,
		children: <Text style={SM_TEXT}>{value}</Text>,
	}));

const formatInstant = (iso: string | null | undefined): string => (iso ? formatInZone(iso, "D MMM YYYY HH:mm") : "") || "—";

const humanise = (value: string): string => value.replace(/_/g, " ");

const count = (value: number | null | undefined): string => (value === null || value === undefined ? "—" : String(value));

// gene_codes stores call rates as percentages; the genotyper helpers expect the 0–1 fraction.
const percentCallRate = (value: number | null | undefined): string =>
	value === null || value === undefined ? "—" : formatCallRate(value / 100);

const isLowPercentCallRate = (value: number | null | undefined): boolean =>
	value !== null && value !== undefined && isLowCallRate(value / 100);

const processingTypeLabel = (result: SampleResult): string | null => {
	const key: string | undefined = result.analysis_results?.processing_type ?? result.resultable_type;
	return key ? PROCESSING_TYPE_LABELS[key] ?? humanise(key) : null;
};

const fileLabel = (result: SampleResult, index: number): string => result.files_meta?.[index]?.name ?? `File ${index + 1}`;

const fileSaveName = (result: SampleResult, index: number): string => result.files_meta?.[index]?.name ?? `file-${index + 1}`;

const saveBlob = (blob: Blob, name: string): void => {
	const url = window.URL.createObjectURL(blob);
	const anchor = document.createElement("a");
	anchor.href = url;
	anchor.download = name;
	document.body.appendChild(anchor);
	anchor.click();
	document.body.removeChild(anchor);
	window.URL.revokeObjectURL(url);
};

const ResultFiles: React.FC<{ result: SampleResult }> = ({ result }) => {
	const { showError } = useNotifications();
	const [downloadingIndex, setDownloadingIndex] = useState<number | null>(null);
	const files = result.files ?? [];

	const handleDownload = async (index: number) => {
		setDownloadingIndex(index);
		try {
			const blob = await downloadResultFile(result.id, index);
			saveBlob(blob, fileSaveName(result, index));
		} catch (err) {
			showError(`${fileLabel(result, index)} could not be downloaded`);
		} finally {
			setDownloadingIndex(null);
		}
	};

	return (
		<div data-testid="results-summary-files">
			<SectionLabel>Files</SectionLabel>
			{files.length === 0 ? (
				<Text type="secondary" style={{ fontSize: 11 }}>
					No files attached
				</Text>
			) : (
				<div style={{ border: "1px solid var(--mg-border)", borderRadius: "var(--mg-radius-sm)" }}>
					{files.map((_, index) => {
						const size = result.files_meta?.[index]?.size;
						const label = fileLabel(result, index);
						return (
							<div
								key={index}
								style={{
									display: "flex",
									alignItems: "center",
									justifyContent: "space-between",
									gap: 8,
									padding: "4px 10px",
									borderBottom: index === files.length - 1 ? undefined : "1px solid var(--mg-border)",
								}}
							>
								<span style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0, fontSize: 12 }}>
									<FileTextOutlined style={{ color: "var(--mg-text-secondary)" }} />
									<span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{label}</span>
									{typeof size === "number" && (
										<Text type="secondary" style={{ fontSize: 10, flexShrink: 0 }}>
											{formatFileSize(size)}
										</Text>
									)}
								</span>
								<Button
									type="text"
									size="small"
									icon={<DownloadOutlined />}
									aria-label={`Download ${label}`}
									loading={downloadingIndex === index}
									onClick={() => handleDownload(index)}
									data-testid={`results-summary-download-${index}`}
								/>
							</div>
						);
					})}
				</div>
			)}
		</div>
	);
};

interface GeneCodesSampleRow extends Partial<GeneCodesSampleSummary> {
	sample_id: string;
}

const geneCodesSampleColumns: ColumnsType<GeneCodesSampleRow> = [
	{
		title: "Sample",
		dataIndex: "sample_id",
		key: "sample_id",
		width: 120,
		render: (id: string) => <Text style={{ fontSize: 12, fontWeight: 500 }}>{id}</Text>,
	},
	{
		title: "SNPs",
		dataIndex: "total_snps",
		key: "total_snps",
		width: 90,
		render: (value: number | undefined) => <Text style={{ ...SM_TEXT, fontVariantNumeric: "tabular-nums" }}>{count(value)}</Text>,
	},
	{
		title: "Call rate",
		dataIndex: "call_rate",
		key: "call_rate",
		width: 90,
		render: (value: number | undefined) => (
			<Text
				style={{
					...SM_TEXT,
					fontVariantNumeric: "tabular-nums",
					color: isLowPercentCallRate(value) ? "var(--mg-warning)" : undefined,
					fontWeight: isLowPercentCallRate(value) ? 600 : undefined,
				}}
			>
				{percentCallRate(value)}
			</Text>
		),
	},
	{
		title: "No-calls",
		dataIndex: "no_call_count",
		key: "no_call_count",
		width: 90,
		render: (value: number | undefined) => <Text style={{ ...SM_TEXT, fontVariantNumeric: "tabular-nums" }}>{count(value)}</Text>,
	},
];

const GeneCodesFeedback: React.FC<{ data: GeneCodesAnalysisResults }> = ({ data }) => {
	const overall = data.overall_statistics ?? {};
	const rows: GeneCodesSampleRow[] = Object.entries(data.sample_statistics ?? {}).map(([sampleId, stats]) => ({
		sample_id: sampleId,
		...(stats?.summary_statistics ?? {}),
	}));
	const errors = data.errors ?? [];

	return (
		<div style={{ display: "flex", flexDirection: "column", gap: 8 }} data-testid="results-summary-gene-codes">
			<Descriptions
				size="small"
				column={3}
				colon={false}
				items={descItems([
					["Samples processed", count(overall.total_samples_processed ?? data.total_samples)],
					["Records processed", count(data.records_processed)],
					["Processing time", data.total_processing_time === undefined ? "—" : `${data.total_processing_time}s`],
					["Average call rate", percentCallRate(overall.average_call_rate)],
					["Min call rate", percentCallRate(overall.min_call_rate)],
					["Max call rate", percentCallRate(overall.max_call_rate)],
				])}
			/>
			{rows.length > 0 && (
				<DataTable bare columns={geneCodesSampleColumns} dataSource={rows} rowKey="sample_id" pagination={false} scrollX="max-content" data-testid="results-summary-samples" />
			)}
			{errors.length > 0 && (
				<Alert
					type="error"
					showIcon
					data-testid="results-summary-errors"
					message={`${errors.length} error${errors.length === 1 ? "" : "s"} during analysis`}
					description={
						<ul style={{ margin: 0, paddingLeft: 16, fontSize: 11 }}>
							{errors.map((error, index) => (
								<li key={index}>{error}</li>
							))}
						</ul>
					}
				/>
			)}
		</div>
	);
};

const GsaRawFeedback: React.FC<{ summary: GsaRawAnalysisSummary }> = ({ summary }) => {
	const warningCount = Array.isArray(summary.warnings) ? summary.warnings.length : summary.warnings;
	const pairs: DescPair[] = [
		["Run", summary.run_id ? `#${summary.run_id}` : "—"],
		["Run status", summary.status ? humanise(summary.status) : "—"],
		["Samples", count(summary.samples)],
		["SNP import", summary.snp_status ? humanise(summary.snp_status) : "—"],
		["Analysis", summary.analysis_status ? humanise(summary.analysis_status) : "—"],
		["CNV rows", count(summary.cnv_rows)],
		["PGx rows", count(summary.pgx_rows)],
		["mtDNA rows", count(summary.mtdna_rows)],
		["No signal", count(summary.no_signal)],
		["Warnings", count(warningCount)],
	];
	if (summary.error_code) pairs.push(["Error code", summary.error_code]);
	return <Descriptions size="small" column={4} colon={false} items={descItems(pairs)} data-testid="results-summary-gsa-raw" />;
};

const formatValue = (value: unknown): string => {
	if (value === null || value === undefined || value === "") return "—";
	return typeof value === "object" ? JSON.stringify(value) : String(value);
};

const ManualEntryFeedback: React.FC<{ data: Record<string, unknown> }> = ({ data }) => {
	const entries = Object.entries(data);
	if (entries.length === 0) {
		return (
			<Text type="secondary" style={{ fontSize: 11 }}>
				No data entered
			</Text>
		);
	}
	return (
		<Descriptions
			size="small"
			column={2}
			colon={false}
			items={descItems(entries.map(([key, value]): DescPair => [humanise(key), formatValue(value)]))}
			data-testid="results-summary-manual-entry"
		/>
	);
};

const UploadedResultSummary: React.FC<UploadedResultSummaryProps> = ({ result, onRerun }) => {
	const analysis = result.analysis_results;
	const isGsaRaw = result.result_type === "gsa_raw";
	const geneCodes = (result.result_type === "gene_codes" || isGsaRaw) && isGeneCodesAnalysisResults(analysis) ? analysis : null;
	const gsaSummary = isGsaRaw && isGsaRawAnalysisSummary(analysis) ? analysis : null;
	const manualData = result.result_type === "manual_entry" ? (result.data ?? {}) : null;
	const failure = result.status === "failed" ? (typeof analysis?.error === "string" ? analysis.error : null) : null;
	const processingType = processingTypeLabel(result);
	const hasFeedback = result.status === "processing" || result.status === "failed" || !!geneCodes || !!gsaSummary || !!manualData || isGsaRaw;

	return (
		<div data-testid="results-summary" style={{ display: "flex", flexDirection: "column", gap: 12, padding: "4px 0" }}>
			<div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
				<Text style={{ fontSize: 12, fontWeight: 600 }}>{getResultTypeLabel(result.result_type)}</Text>
				<StatusTag status={result.status || "pending"} size="small" />
				<StatusTag status={result.verification_status || "pending"} label={VERIFICATION_LABELS[result.verification_status] ?? result.verification_status} size="small" />
				<Text type="secondary" style={{ fontSize: 11 }}>
					Uploaded {formatInstant(result.created_at)} · by user #{result.uploaded_by}
					{processingType && ` · ${processingType}`}
				</Text>
			</div>

			<ResultFiles result={result} />

			<div>
				<SectionLabel>Notes</SectionLabel>
				<Text style={{ fontSize: 12, whiteSpace: "pre-wrap" }} type={result.notes ? undefined : "secondary"}>
					{result.notes || "—"}
				</Text>
			</div>

			{hasFeedback && (
				<div data-testid="results-summary-feedback" style={{ display: "flex", flexDirection: "column", gap: 8 }}>
					<SectionLabel>Analysis feedback</SectionLabel>
					{result.status === "processing" && (
						<Text style={{ fontSize: 12, display: "inline-flex", alignItems: "center", gap: 8 }}>
							<Spin size="small" /> Analysis running…
						</Text>
					)}
					{result.status === "failed" && <Alert type="error" showIcon message="Analysis failed" description={failure ?? undefined} data-testid="results-summary-failed" />}
					{geneCodes && <GeneCodesFeedback data={geneCodes} />}
					{manualData && <ManualEntryFeedback data={manualData} />}
					{gsaSummary && <GsaRawFeedback summary={gsaSummary} />}
					{isGsaRaw && <GenotyperRunPanel resultId={result.id} onRerun={onRerun} />}
				</div>
			)}
		</div>
	);
};

export default UploadedResultSummary;
